import React, { useContext, useEffect, useState } from 'react';
import authContext from '../../../../../AuthContext/authContext';
import { STAFF_VERIFICATION_EVENT, staffSessionRequest } from '../../../../Graph/Authentication/staffSessionClient';

// UX only. The backend, not this component or role cookies, grants staff access.
export default function StaffSessionGate({ children,
  enabled = process.env.REACT_APP_STAFF_SESSION_VERIFICATION_REQUIRED === 'true' }) {
  const { token, baseUrl, logout } = useContext(authContext);
  const [state, setState] = useState({ token: null, phase: 'checking' });
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  const [nextSend, setNextSend] = useState(0);
  const activeGate = enabled || retry > 0;
  useEffect(() => {
    if (!nextSend) return undefined;
    const timer = setTimeout(() => setNextSend(0), Math.max(0, nextSend - Date.now()));
    return () => clearTimeout(timer);
  }, [nextSend]);
  useEffect(() => {
    const demand = () => setRetry(value => value + 1);
    window.addEventListener(STAFF_VERIFICATION_EVENT, demand);
    return () => window.removeEventListener(STAFF_VERIFICATION_EVENT, demand);
  }, []);
  useEffect(() => {
    if (!token || !activeGate) { setState({ token, phase: 'allowed' }); return undefined; }
    let live = true;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    setState({ token, phase: 'checking' }); setCode(''); setError(''); setNextSend(0);
    staffSessionRequest(baseUrl, token, '', undefined, { signal: controller.signal, requiredOnLegacy: true }).then(result => {
      if (live) setState({ token, phase: result.required && !result.verified ? 'challenge' : 'allowed' });
    }).catch(err => {
      if (live) { setState({ token, phase: 'error' }); setError(err.message); }
    }).finally(() => clearTimeout(timer));
    return () => { live = false; controller.abort(); clearTimeout(timer); };
  }, [token, baseUrl, retry, activeGate]);

  async function perform(action) {
    if (busy) return;
    setBusy(true); setError('');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const result = await staffSessionRequest(baseUrl, token, action,
        action === '/verify' ? { code } : { method: 'email' }, { signal: controller.signal });
      if (action === '/logout') logout();
      else if (action === '/verify' && result.verified === true) {
        setCode(''); setRetry(value => value + 1); // Re-read server state, never trust a browser flag.
      } else if (action === '/challenge') {
        if (result.verified) setRetry(value => value + 1);
        else { setState({ token, phase: 'code' }); setNextSend(Date.now() + 60000); }
      }
    } catch (err) { setError(err.message || 'Verification failed.'); }
    finally { clearTimeout(timer); setBusy(false); }
  }
  if (!token || !activeGate || (state.token === token && state.phase === 'allowed')) return children;
  const checking = state.token !== token || state.phase === 'checking';
  return <main className="main-content" style={{ maxWidth: 560, margin: '6rem auto', padding: 24 }}>
    <section aria-labelledby="staff-verification-title">
      <h1 id="staff-verification-title">Verify your staff sign-in</h1>
      {checking ? <p role="status">Checking this session…</p> : <>
        <p>Admin and moderator sessions require an email code. Verification applies only to this sign-in, not other browsers or accounts.</p>
        {state.phase === 'code' && <form onSubmit={event => { event.preventDefault(); perform('/verify'); }}>
          <label htmlFor="staff-session-code">Six-digit code sent to your account email</label>
          <input id="staff-session-code" type="text" inputMode="numeric" autoComplete="one-time-code"
            value={code} maxLength={6} onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} />
          <button type="submit" disabled={busy || !/^\d{6}$/.test(code)}>Verify session</button>
        </form>}
        {state.phase === 'error' ? <button disabled={busy} onClick={() => setRetry(value => value + 1)}>Retry session check</button> :
          <button disabled={busy || Date.now() < nextSend} onClick={() => perform('/challenge')}>
            {state.phase === 'code' ? 'Resend email code (wait 60 seconds)' : 'Send email code'}
          </button>}
        <button disabled={busy} onClick={() => perform('/logout')}>Sign out</button>
        <p>Codes expire in 10 minutes. Never share a code. SMS is not enabled yet.</p>
      </>}
      {error && <p role="alert">{error}</p>}
    </section>
  </main>;
}
